/**
 * Authenticated layout — pure auth gate. Runs on every navigation:
 *   1. fetches /api/me (attaches Bearer from storage)
 *   2. on 401, redirect to /login?from=...
 *   3. on `mustChangePassword: true` and current path != /change-password,
 *      redirect to /change-password
 *   4. otherwise render <Outlet /> as the only child
 *
 * The /login and /change-password routes opt out of the layout via direct
 * registration on the root in `main.tsx`. The designv2 Nav (rendered inside
 * App.tsx) is the only top chrome — this layout adds none of its own.
 */
import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { UnauthorizedError, apiFetch } from "../lib/api";
import { clearTokens, loadTokens } from "../lib/auth-storage";

export interface Me {
  id: number;
  name: string;
  email: string;
  sysRole: "admin" | "lead" | "pm" | "accountant";
  emp: "full" | "part";
  wl: number;
  roles: string[];
  periods: Array<{ from: string; to: string; wl: number }>;
  mustChangePassword: boolean;
}

export function AuthenticatedLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void reloadKey;
    async function load() {
      if (!loadTokens()) {
        if (location.pathname === "/login") return;
        await navigate({ to: "/login", search: { from: location.pathname } });
        return;
      }
      try {
        const res = await apiFetch("/api/me");
        if (cancelled) return;
        const body = (await res.json()) as Me;
        setMe(body);
        setLoadError(false);
        if (body.mustChangePassword && location.pathname !== "/change-password") {
          await navigate({ to: "/change-password" });
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof UnauthorizedError) {
          clearTokens();
          await navigate({ to: "/login", search: { from: location.pathname } });
          return;
        }
        setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate, reloadKey]);

  if (loading) return null;
  if (loadError) {
    return (
      <main style={{ maxWidth: 480, margin: "10vh auto" }}>
        <h1 style={{ fontSize: 22, marginBottom: 12 }}>Couldn't load your profile</h1>
        <p style={{ color: "#374151", marginBottom: 16 }}>
          The server didn't respond. Check your connection and try again.
        </p>
        <button
          type="button"
          onClick={() => {
            setLoadError(false);
            setLoading(true);
            setReloadKey((k) => k + 1);
          }}
          style={{ padding: "8px 16px" }}
        >
          Retry
        </button>
      </main>
    );
  }
  if (!me) return null;

  return <Outlet />;
}
